import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AttachedBlocks } from '../components/cms/AttachedBlocks'
import { Footer, Header } from '../components/landing'
import { api } from '../core/trpc'
import { formatPrice } from '../utils/formatPrice'
import { buildOgMeta } from '../utils/og'
import { longFormRichTextClassName } from '../utils/richText'
import { withoutCmsBlocks } from '../utils/withoutCmsBlocks'

const LifestyleProgramDetailsPage = () => {
    const { t } = useTranslation()
    const { programId } = Route.useParams()
    const { data: program, isLoading } = api.lifestyleProgram.getPublic.useQuery({
        id: programId,
    })

    const hasDiscount =
        typeof program?.originalPrice === 'number' &&
        program.originalPrice > 0 &&
        program.originalPrice > program.price

    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="bg-white py-12">
                    <div className="mx-auto w-full max-w-[960px] px-6">
                        <Link
                            className="mb-8 inline-flex items-center gap-1 text-sm font-medium text-brand-gray transition-colors hover:text-primary"
                            to="/lifestyle-programs"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            {t('frontend.lifestylePrograms.backToList')}
                        </Link>

                        {isLoading ? (
                            <div>
                                <Skeleton className="mb-4 h-8 w-64" />
                                <Skeleton className="mb-8 h-6 w-32" />
                                <Skeleton className="h-40 w-full" />
                            </div>
                        ) : !program ? (
                            <div className="py-24 text-center">
                                <p className="text-lg text-brand-gray">
                                    {t('frontend.lifestylePrograms.notFound')}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <h1 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                                    {program.name}
                                </h1>

                                <div className="mb-8 flex items-baseline gap-3">
                                    {hasDiscount && (
                                        <span className="text-lg font-medium text-brand-gray line-through">
                                            {formatPrice(program.originalPrice ?? 0)}
                                        </span>
                                    )}
                                    <span className="text-2xl font-bold text-brand-dark">
                                        {formatPrice(program.price)}
                                    </span>
                                </div>

                                <AttachedBlocks blocks={program.cmsBlocks} className="mb-6" />
                                {program.description && (
                                    <div
                                        className={longFormRichTextClassName}
                                        dangerouslySetInnerHTML={{ __html: program.description }}
                                    />
                                )}

                                <div className="mt-10 flex flex-wrap gap-4 border-t border-slate-100 pt-8">
                                    <a
                                        href={`/checkout?programId=${program.id}`}
                                        className="inline-block rounded-full bg-primary px-8 py-3 font-bold text-white transition-colors hover:bg-primary/90"
                                    >
                                        {t('frontend.lifestylePrograms.apply')}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/lifestyle-programs_/$programId')({
    component: LifestyleProgramDetailsPage,
    loader: async ({ context, params }) => {
        const [program, websiteSettings] = await Promise.allSettled([
            context.apiUtils.lifestyleProgram.getPublic.ensureData({ id: params.programId }),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            program:
                program.status === 'fulfilled' && program.value
                    ? withoutCmsBlocks(program.value)
                    : null,
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
            programId: params.programId,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: loaderData?.program?.name
                ? `Long-Y - ${loaderData.program.name}`
                : 'Long-Y - Életmód program',
            url: `${import.meta.env.VITE_WEB_URL || ''}/lifestyle-programs/${loaderData?.programId ?? ''}`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
